import { useState, useCallback, useEffect } from 'react';
import timeaway from '../../network/timeaway';
import BookRecurringTimeAway from './components/BookRecurringTimeAway';
import BookTimeAway from './components/BookTimeAway';
import { TimeawayTable } from './components/TimeawayTable';

export function TimeAwayRoute() {
  const [allocatedTimeaway, setAllocatedTimeaway] = useState<any>(null);

  const [myTimeaway, setMyTimeaway] = useState([]);

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [showRecurringBooking, setShowRecurringBooking] = useState(false);

  const refreshAllowance = useCallback(async () => {
    try {
      const response: any = await timeaway.getTimeAway();
      if (response?.data?.allowance) {
        setAllocatedTimeaway(response.data.allowance);
      } else {
        console.warn('Allowance data not found in response:', response);
        setAllocatedTimeaway({});
      }
    } catch (error) {
      console.error('Error fetching time away allowance: ', error);
      setAllocatedTimeaway({});
    }
  }, []);

  const refreshMyTimeaway = useCallback(
    async (year: number) => {
      try {
        const { data: response } = await timeaway.getPersonalTimesOff(year);
        setMyTimeaway(response);
      } catch (error) {
        console.error('Error fetching personal time off requests: ', error);
        setMyTimeaway([]);
      }
    },
    [],
  );

  const refreshAll = useCallback(async () => {
    await Promise.all([refreshAllowance(), refreshMyTimeaway(selectedYear)]);
  }, [refreshAllowance, refreshMyTimeaway, selectedYear]);

  const handleCancelRequest = useCallback(
    async (timeawayId: string) => {
      const confirmed = window.confirm(
        'Cancel this time away request? This cannot be undone.',
      );
      if (!confirmed) return;
      await timeaway.deleteTimeawayRequest(timeawayId);
      await refreshAll();
    },
    [refreshAll],
  );

  useEffect(() => {
    refreshAllowance();
  }, [refreshAllowance]);

  useEffect(() => {
    refreshMyTimeaway(selectedYear);
  }, [refreshMyTimeaway, selectedYear]);

  if (allocatedTimeaway === null) {
    return <div>Loading time away information...</div>;
  }

  return (
    <>
      <div>
        <div className="mt-3 md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <div className="px-4 sm:px-0">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Book time away
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Book time away pending approval
              </p>
            </div>
          </div>
          <div className="mt-5 md:col-span-2 md:mt-0">
            <BookTimeAway onBooked={refreshAll} />
            <div className="mt-3 text-right">
              <button
                type="button"
                className="text-sm font-medium text-red-600 hover:text-red-700"
                aria-expanded={showRecurringBooking}
                onClick={() => setShowRecurringBooking((open) => !open)}
              >
                {showRecurringBooking
                  ? 'Hide recurring booking'
                  : 'Book the same days every week'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden sm:block" aria-hidden="true">
        <div className="py-5">
          <div className="border-t border-gray-200" />
        </div>
      </div>

      {showRecurringBooking && (
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <div className="px-4 sm:px-0">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Book recurring time away
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                The same weekdays every week, for example working remotely
                on Fridays. Each day is sent for approval.
              </p>
            </div>
          </div>
          <div className="mt-5 md:col-span-2 md:mt-0">
            <BookRecurringTimeAway onBooked={refreshAll} />
          </div>
          <div className="hidden sm:block md:col-span-3" aria-hidden="true">
            <div className="py-5">
              <div className="border-t border-gray-200">
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-10 sm:mt-0">
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <div className="px-4 sm:px-0">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                My time away
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                See your current time away
              </p>
            </div>
          </div>
          <div className="mt-5 md:col-span-2 md:mt-0">
            <div className="space-y-6 bg-white px-4 py-5 sm:p-6">
              <div className="col-span 12 sm:col-span-12">
                <label
                  htmlFor="year"
                  className="block text-sm font-medium text-gray-700"
                >
                  My time away in
                </label>
                <select
                  id="year"
                  name="year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  aria-label="Select year for time away information"
                  className="mt-2 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
                >
                  {Array.from({ length: 3 }, (_, i) => (
                    <option key={2024 + i} value={2024 + i}>
                      {2024 + i}
                    </option>
                  ))}
                </select>
                <div className="mt-8 grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-3">
                    <label
                      htmlFor="first-name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Holiday (paid)
                    </label>
                    <h3 className="mt-2">
                      {allocatedTimeaway?.paid_holiday?.used ?? 0} /{' '}
                      {allocatedTimeaway?.paid_holiday?.allowed?.value ?? 0} days
                      {' '}({allocatedTimeaway?.paid_holiday?.remaining ?? allocatedTimeaway?.paid_holiday?.allowed?.value ?? 0} remaining)
                    </h3>
                    {allocatedTimeaway?.paid_holiday?.carried_over > 0 && (
                      <p className="mt-1 text-xs text-gray-500">
                        Includes {allocatedTimeaway.paid_holiday.carryover_available}{' '}
                        of {allocatedTimeaway.paid_holiday.carried_over} day(s)
                        carried over from last year
                        {allocatedTimeaway.paid_holiday.carryover_expires_at
                          ? ` (use by ${new Date(
                              allocatedTimeaway.paid_holiday.carryover_expires_at,
                            ).toLocaleDateString('en-GB')})`
                          : ''}
                        .
                      </p>
                    )}
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <label
                      htmlFor="last-name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Holiday (unpaid)
                    </label>
                    <h3 className="mt-2">
                      {allocatedTimeaway?.unpaid_holiday?.used ?? 0} /{' '}
                      {allocatedTimeaway?.unpaid_holiday?.allowed?.value ?? 0} days
                      {' '}({allocatedTimeaway?.unpaid_holiday?.remaining ?? allocatedTimeaway?.unpaid_holiday?.allowed?.value ?? 0} remaining)
                    </h3>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-3">
                    <label
                      htmlFor="first-name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Remote working per year
                    </label>
                    <h3 className="mt-2">
                      {allocatedTimeaway?.working_remotely?.used ?? 0} /{' '}
                      {allocatedTimeaway?.working_remotely?.allowed?.value ?? 0} days
                      {' '}({allocatedTimeaway?.working_remotely?.remaining ?? allocatedTimeaway?.working_remotely?.allowed?.value ?? 0} remaining)
                    </h3>
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <label
                      htmlFor="last-name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Paid sick leave per year
                    </label>
                    <h3 className="mt-2">
                      {allocatedTimeaway?.sick_leave?.used ?? 0} /{' '}
                      {allocatedTimeaway?.sick_leave?.allowed?.value ?? 0} days
                      {' '}({allocatedTimeaway?.sick_leave?.remaining ?? allocatedTimeaway?.sick_leave?.allowed?.value ?? 0} remaining)
                    </h3>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-3">
                    <label
                      htmlFor="other-leave"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Other Leave
                    </label>
                    <h3 className="mt-2">
                      {allocatedTimeaway?.other_leave?.used ?? 0} /{' '}
                      {allocatedTimeaway?.other_leave?.allowed?.value ?? 0} days
                      {' '}({allocatedTimeaway?.other_leave?.remaining ?? allocatedTimeaway?.other_leave?.allowed?.value ?? 0} remaining)
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-200 pt-4">
          <TimeawayTable myTimeaway={myTimeaway} onCancelRequest={handleCancelRequest} />
        </div>
      </div>
    </>
  );
}
